import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { ticketsApi } from '@/lib/api';
import { ridesApi } from '@/lib/rides-api';
import { passesApi } from '@/lib/passes-api';
import { queryKeys, rideKeys, passKeys } from '@/lib/query-keys';
import { formatDateTime, formatMoney, statusVariant } from '@/lib/format';
import type { BadgeVariant } from '@/lib/format';
import { rideStatusText, rideStatusVariant, vehicleLabel } from '@/lib/ride-format';
import { passStatusText } from '@/lib/passes-format';
import { Badge } from '@/components/shared/badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ErrorState } from '@/components/shared/error-state';
import { SkeletonRow } from '@/components/shared/skeleton';
import { Screen, AccountButton } from '@/components/chrome';
import { LeadIcon } from '@/components/ui/lead-icon';
import { CategoryTag } from '@/components/ui/category-tag';
import type { Category } from '@/components/ui/category';

export const Route = createFileRoute('/_authed/trips/')({
  component: TripsPage,
});

interface TripRow {
  kind: 'metro' | 'ride' | 'pass';
  category: Category;
  id: string;
  title: string;
  meta: string;
  statusText: string;
  badge: BadgeVariant;
  at: number;
}

function TripsPage() {
  const ticketsQuery = useQuery({
    queryKey: queryKeys.list(),
    queryFn: () => ticketsApi.listTickets(),
  });
  const ridesQuery = useQuery({
    queryKey: rideKeys.list(),
    queryFn: () => ridesApi.list(),
  });
  const passesQuery = useQuery({
    queryKey: passKeys.list(),
    queryFn: () => passesApi.list(),
  });

  // Skeleton only until the FIRST category resolves, then paint what we have and let
  // the others fill in — so metro isn't blocked behind rides/passes 503-retrying when
  // TRV10/TRV14 are off.
  if (ticketsQuery.isLoading && ridesQuery.isLoading && passesQuery.isLoading) {
    return (
      <Screen title="My Trips" right={<AccountButton />}>
        <div className="wb-col wb-gap-12">
          {[0, 1, 2].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </Screen>
    );
  }

  // Only hard-fail if ALL categories error; one outage still shows the others.
  if (ticketsQuery.isError && ridesQuery.isError && passesQuery.isError) {
    return (
      <Screen title="My Trips" right={<AccountButton />}>
        <ErrorState
          error={ticketsQuery.error ?? ridesQuery.error ?? passesQuery.error}
          onRetry={() => {
            void ticketsQuery.refetch();
            void ridesQuery.refetch();
            void passesQuery.refetch();
          }}
        />
      </Screen>
    );
  }

  const rows: TripRow[] = [
    ...(ticketsQuery.data?.tickets ?? []).map((t): TripRow => {
      const bus = t.journey.vehicleCategory === 'BUS';
      return {
        kind: 'metro',
        category: bus ? 'bus' : 'metro',
        id: t.id,
        title: `${t.journey.from.name} → ${t.journey.to.name}`,
        meta: [t.ticketType, formatMoney(t.fare), formatDateTime(t.purchasedAt)]
          .filter(Boolean)
          .join(' · '),
        statusText: t.status,
        badge: statusVariant(t.status),
        at: Date.parse(t.purchasedAt ?? '') || 0,
      };
    }),
    ...(ridesQuery.data?.rides ?? []).map(
      (r): TripRow => ({
        kind: 'ride',
        category: 'ride',
        id: r.id,
        title: vehicleLabel(r.vehicle?.category, r.vehicle?.variant),
        meta: [formatMoney(r.quote?.total ?? r.fare), formatDateTime(r.bookedAt)]
          .filter(Boolean)
          .join(' · '),
        statusText: rideStatusText(r.rideState, r.status),
        badge: rideStatusVariant(r.rideState, r.status),
        at: Date.parse(r.bookedAt ?? '') || 0,
      })
    ),
    ...(passesQuery.data?.passes ?? []).map(
      (p): TripRow => ({
        kind: 'pass',
        category: 'pass',
        id: p.id,
        title: p.name || p.provider?.name || 'Entry pass',
        meta: [formatMoney(p.fare), formatDateTime(p.purchasedAt)].filter(Boolean).join(' · '),
        statusText: passStatusText(p.status),
        badge: statusVariant(p.status),
        at: Date.parse(p.purchasedAt ?? '') || 0,
      })
    ),
  ].sort((a, b) => b.at - a.at);

  const to = (row: TripRow) =>
    row.kind === 'metro' ? '/metro/$id' : row.kind === 'ride' ? '/rides/$id' : '/passes/$id';

  return (
    <Screen title="My Trips" right={<AccountButton />}>
      {rows.length === 0 ? (
        <EmptyState
          icon="ticket"
          title="No trips yet"
          description="Book a metro ticket, a ride or an entry pass to see it here."
          action={{ label: 'Book', onClick: () => (window.location.href = '/') }}
        />
      ) : (
        <div className="wb-col wb-gap-12 wb-stagger">
          {rows.map((row) => (
            <Link
              key={`${row.kind}:${row.id}`}
              to={to(row)}
              params={{ id: row.id }}
              className={`wb-listrow cat-${row.category}`}
            >
              <LeadIcon category={row.category} />
              <div className="wb-grow" style={{ minWidth: 0 }}>
                <div className="wb-row wb-between wb-gap-8">
                  <span
                    className="wb-strong"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.title}
                  </span>
                  {row.statusText && <Badge variant={row.badge}>{row.statusText}</Badge>}
                </div>
                <div className="wb-row wb-gap-8" style={{ marginTop: 4 }}>
                  <CategoryTag category={row.category} />
                  {row.meta && <span className="wb-tiny">{row.meta}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Screen>
  );
}
